import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
} from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import useWhatsAppModalStyles from "../WhatsAppModal/whatsAppModalStyles";
import useWhatsAppModal from "../WhatsAppModal/useWhatsAppModal";
import TabPanel from "../WhatsAppModal/TabPanel";
import TransferTab from "../WhatsAppModal/TransferTab";
import AutomationSection from "../WhatsAppModal/AutomationSection";
import ConfigChangePreviewModal from "../ConfigChangePreviewModal";
import ConnectionWarningModal from "../ConnectionWarningModal";
import ConfirmationModal from "../ConfirmationModal";
import { COMPACT_MENU } from "../BaileysConnectionModal/compactMenu";
import { getTelegramUserConnection, logoutTelegramUser } from "../../services/telegram.api";

/**
 * Editar una conexión de Telegram CUENTA PERSONAL (doc 0438 Parte V, TU5). Misma maqueta y misma
 * lógica que el modal de API Baileys (0439): `useWhatsAppModal`, enrutado y transferencias. Lo
 * propio de Telegram es la cuenta vinculada y «Cerrar sesión». No hay token de API, ni selector de
 * estado, ni historial, ni leads web: no aplican a este canal.
 */

const Schema = Yup.object().shape({
  name: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required("Required"),
});

const ESTADOS = {
  CONNECTED: { label: "Conectada", color: "#2e7d32" },
  qrcode: { label: "Esperando QR", color: "#ed6c02" },
  PASSWORD_REQUIRED: { label: "Esperando contraseña", color: "#ed6c02" },
  OPENING: { label: "Conectando…", color: "#0288d1" },
  DISCONNECTED: { label: "Desconectada", color: "#c62828" },
};

const useStyles = makeStyles((theme) => ({
  paper: { maxWidth: 680 },
  title: { padding: theme.spacing(1.5, 2.5), "& h2": { fontSize: "1.05rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 10 } },
  logo: { width: 26, height: 26 },
  content: {
    padding: theme.spacing(1, 2.5, 2),
    "& .MuiOutlinedInput-root": { fontSize: "0.85rem" },
    "& .MuiInputLabel-outlined": { fontSize: "0.85rem" },
    "& .MuiFormControl-marginDense": { marginTop: 0, marginBottom: 0 },
    "& .MuiFormControlLabel-label": { fontSize: "0.85rem" },
  },
  tabs: { minHeight: 36, marginBottom: theme.spacing(1.5), borderBottom: `1px solid ${theme.palette.divider}` },
  tab: { minHeight: 36, fontSize: "0.8rem" },
  section: {
    margin: theme.spacing(1.5, 0, 0.75),
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  cuenta: {
    padding: theme.spacing(1.25, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  hint: { display: "block", marginTop: 2, lineHeight: 1.35 },
  actions: { padding: theme.spacing(1.25, 2.5) },
}));

const Seccion = ({ children, classes }) => (
  <Grid item xs={12}>
    <Typography className={classes.section}>{children}</Typography>
  </Grid>
);

const fecha = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const TelegramUserEditModal = ({ open, onClose, whatsAppId, onSave }) => {
  const classes = useStyles();
  const theme = useTheme();
  const legacyClasses = useWhatsAppModalStyles();
  const [cuenta, setCuenta] = useState(null);
  const [confirmarSalida, setConfirmarSalida] = useState(false);
  const [saliendo, setSaliendo] = useState(false);

  useEffect(() => {
    if (!open || !whatsAppId) return undefined;
    let vivo = true;
    getTelegramUserConnection(whatsAppId)
      .then(({ data }) => vivo && setCuenta(data))
      .catch(() => vivo && setCuenta(null));
    return () => {
      vivo = false;
    };
  }, [open, whatsAppId]);

  const {
    activeTab,
    connectionWarningData,
    connectionWarningOpen,
    flowBuilders,
    formikInitialValues,
    handleChangeIntegration,
    handleChangePrompt,
    handleChangeQueue,
    handleCheckboxChange,
    handleClose,
    handleConfirmConfigChange,
    handleSaveWhatsApp,
    handleTabChange,
    isLoading,
    loadingFlowBuilders,
    loadingWhatsapps,
    previewData,
    previewModalOpen,
    selectedFlowBuilder,
    selectedIntegration,
    selectedPrompt,
    selectedQueueId,
    setActiveTab,
    setConnectionWarningData,
    setConnectionWarningOpen,
    setPendingWhatsAppData,
    setPreviewData,
    setPreviewModalOpen,
    setSelectedFlowBuilder,
    setSelectedIntegration,
    setSelectedPrompt,
    setSelectedQueueId,
    setTransferQueueId,
    setTransferWhatsappId,
    tabStates,
    transferQueueId,
    transferWhatsappId,
    whatsapps,
  } = useWhatsAppModal({ open, onClose, whatsAppId, onSave, defaultProvider: "telegram_user" });

  const cerrarSesion = async () => {
    setSaliendo(true);
    try {
      await logoutTelegramUser(whatsAppId);
      toast.success("Sesión de Telegram cerrada. Para volver a usar la conexión, genera un QR nuevo.");
      onSave && onSave();
    } catch (err) {
      toastError(err);
    } finally {
      setSaliendo(false);
      setConfirmarSalida(false);
    }
  };

  const estado = ESTADOS[cuenta?.connectionStatus] || ESTADOS.DISCONNECTED;
  const vinculada = Boolean(cuenta?.telegramUserId);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper" classes={{ paper: classes.paper }} disableEnforceFocus disableAutoFocus disableRestoreFocus>
        <DialogTitle className={classes.title}>
          <img src="/telegram.png" alt="" className={classes.logo} />
          Editar conexión — Telegram
        </DialogTitle>

        <Formik
          key={whatsAppId}
          initialValues={formikInitialValues}
          enableReinitialize
          validationSchema={Schema}
          onSubmit={async (values, actions) => {
            if (!values.name || values.name.trim().length === 0) {
              setActiveTab(0);
              actions.setErrors({ name: "Required" });
              actions.setSubmitting(false);
              return;
            }
            await handleSaveWhatsApp(values);
            actions.setSubmitting(false);
          }}
        >
          {({ values, isSubmitting, setFieldValue }) => {
            const ocupado = isSubmitting || isLoading || saliendo;
            return (
              <Form>
                <DialogContent dividers className={classes.content}>
                  <Tabs value={activeTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" className={classes.tabs}>
                    <Tab label="Conexión" className={classes.tab} />
                    <Tab label="Transferencias" className={classes.tab} />
                  </Tabs>

                  <TabPanel value={activeTab} index={0}>
                    <Grid container spacing={1}>
                      <Seccion classes={classes}>Cuenta de Telegram</Seccion>
                      <Grid item xs={12}>
                        <Box className={classes.cuenta}>
                          <Box>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                              {vinculada ? cuenta.displayName || "Cuenta de Telegram" : "Sin cuenta vinculada"}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {vinculada
                                ? `${cuenta.username ? `@${cuenta.username} · ` : ""}id ${cuenta.telegramUserId} · vinculada ${fecha(cuenta.linkedAt)}`
                                : "Genera un QR desde la tarjeta para vincular una cuenta"}
                            </Typography>
                          </Box>
                          <Chip size="small" label={estado.label} style={{ background: estado.color, color: "#fff", fontWeight: 600 }} />
                        </Box>
                        {cuenta?.lastErrorCode && (
                          <Typography variant="caption" color="error" className={classes.hint}>
                            Último aviso de Telegram: {cuenta.lastErrorCode}
                          </Typography>
                        )}
                      </Grid>

                      <Grid item xs={12} sm={7}>
                        <Field name="name">
                          {({ field, meta }) => (
                            <TextField
                              {...field}
                              label="Nombre de la conexión"
                              variant="outlined"
                              size="small"
                              fullWidth
                              required
                              error={meta.touched && Boolean(meta.error)}
                              helperText={meta.touched && meta.error}
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid item xs={12} sm={5} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                        {vinculada && (
                          <Button size="small" style={{ color: theme.palette.error.main }} disabled={ocupado} onClick={() => setConfirmarSalida(true)}>
                            Cerrar sesión de Telegram
                          </Button>
                        )}
                      </Grid>

                      <Seccion classes={classes}>Enrutado</Seccion>
                      <AutomationSection
                        classes={legacyClasses}
                        selectedQueueId={selectedQueueId}
                        handleChangeQueue={handleChangeQueue}
                        selectedPrompt={selectedPrompt}
                        handleChangePrompt={handleChangePrompt}
                        selectedIntegration={selectedIntegration}
                        handleChangeIntegration={handleChangeIntegration}
                        selectedFlowBuilder={selectedFlowBuilder}
                        setSelectedFlowBuilder={setSelectedFlowBuilder}
                        setSelectedPrompt={setSelectedPrompt}
                        setSelectedIntegration={setSelectedIntegration}
                        setSelectedQueueId={setSelectedQueueId}
                        loadingFlowBuilders={loadingFlowBuilders}
                        flowBuilders={flowBuilders}
                        hasPromptInForm={!!selectedPrompt}
                        hasIntegrationInForm={!!selectedIntegration}
                        hasFlowBuilderInForm={!!selectedFlowBuilder}
                        hasQueueInForm={!!(selectedQueueId && selectedQueueId !== 0)}
                        menuProps={COMPACT_MENU}
                      />
                      <Grid item xs={12}>
                        <FormControl variant="outlined" size="small" fullWidth>
                          <InputLabel id="tg-purpose-label">Para qué sirve esta conexión</InputLabel>
                          <Select
                            labelId="tg-purpose-label"
                            label="Para qué sirve esta conexión"
                            MenuProps={COMPACT_MENU}
                            value={values.type === "internal" ? "internal" : "attention"}
                            onChange={(e) => setFieldValue("type", e.target.value === "internal" ? "internal" : "")}
                          >
                            <MenuItem value="attention">Atención al cliente</MenuItem>
                            <MenuItem value="internal">Cuenta interna o personal</MenuItem>
                          </Select>
                        </FormControl>
                        <Typography variant="caption" color="textSecondary" className={classes.hint}>
                          {values.type === "internal"
                            ? "Sus conversaciones entran al CRM, pero no se reparten ni las contesta el bot. Ojo: una cuenta personal vuelca conversaciones privadas, visibles para los agentes."
                            : "Reparto a departamentos y automatizaciones activas. Los botones del FlowBuilder llegan como menú numerado: Telegram no permite botones desde una cuenta personal."}
                        </Typography>
                      </Grid>
                    </Grid>
                  </TabPanel>

                  <TabPanel value={activeTab} index={1}>
                    <TransferTab
                      classes={legacyClasses}
                      tabStates={tabStates}
                      handleCheckboxChange={handleCheckboxChange}
                      setFieldValue={setFieldValue}
                      transferQueueId={transferQueueId}
                      setTransferQueueId={setTransferQueueId}
                      transferWhatsappId={transferWhatsappId}
                      setTransferWhatsappId={setTransferWhatsappId}
                      loadingWhatsapps={loadingWhatsapps}
                      whatsapps={whatsapps}
                      theme={theme}
                      menuProps={COMPACT_MENU}
                    />
                  </TabPanel>
                </DialogContent>

                <DialogActions className={classes.actions}>
                  <Button onClick={handleClose} disabled={ocupado} size="small">
                    {i18n.t("whatsappModal.buttons.cancel")}
                  </Button>
                  <Button type="submit" color="primary" variant="contained" size="small" disabled={ocupado}>
                    {i18n.t("whatsappModal.buttons.okEdit")}
                    {ocupado && <CircularProgress size={16} style={{ marginLeft: 8 }} />}
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>

      <ConfirmationModal title="¿Cerrar la sesión de Telegram?" open={confirmarSalida} onClose={() => setConfirmarSalida(false)} onConfirm={cerrarSesion}>
        La cuenta dejará de recibir y enviar mensajes desde aquí y desaparecerá de «Dispositivos» en tu Telegram. Los tickets y mensajes ya guardados se conservan. Para volver, genera un QR nuevo.
      </ConfirmationModal>
      <ConnectionWarningModal
        open={connectionWarningOpen}
        onClose={() => {
          setConnectionWarningOpen(false);
          setConnectionWarningData(null);
        }}
        connectionName={connectionWarningData?.connectionName || ""}
        connectionStatus={connectionWarningData?.connectionStatus || ""}
      />
      <ConfigChangePreviewModal
        open={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setPendingWhatsAppData(null);
          setPreviewData(null);
        }}
        previewData={previewData}
        onConfirm={handleConfirmConfigChange}
        loading={isLoading}
        type="whatsapp"
      />
    </>
  );
};

export default React.memo(TelegramUserEditModal);
